import React, { useCallback, useEffect, useState } from "react";
import ChildTransaction from "../../Modals/Payment/ChildTransaction/ChildTransaction";
import { useDispatch, useSelector } from "react-redux";
import {
  walletChildTransaction,
  walletContractApi,
} from "../../../Services/Payments/PaymentApi";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import Styles from "./Contracts.module.css";
import DetailHeader from "../DetailsHeader/DetailHeader";
import { formatDateType } from "../../../commons/FormatDate";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { walletTransaction } from "../../../Redux/Slices/paymentSlice";
import LoadMorePagination from "../../LoadeMore";

const Contracts = ({ walletId: walletIdProp, entityType }) => {
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  ).toLowerCase();

  const activeRole = entityType || role;

  const [contracts, setContracts] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [childTransaction, setChildTransaction] = useState([]);
  const [parentData, setParentData] = useState({});
  const dispatch = useDispatch();
  const wallet = useSelector(
    (state) => state?.paymentSlice?.walletAccountActive,
  );

  const activeWalletId = walletIdProp || wallet?.id;

  useEffect(() => {
    dispatch(walletTransaction({ page: page, size: 20 }));
  }, [dispatch, page]);

  const fetchContracts = useCallback(async (walletId, currentPage) => {
    if (!walletId) return;
    setLoading(true);
    try {
      const res = await walletContractApi({
        id: walletId,
        page: currentPage,
        size: 20,
      });
      if (res?.data?.code === 200) {
        const newData = res.data.data?.page || [];
        setContracts((prev) =>
          currentPage === 0 ? newData : [...prev, ...newData],
        );
        const { totalPages, currentPage: cp } = res.data.data;
        setHasMore(cp < totalPages - 1);
      } else {
        if (currentPage === 0) setContracts([]);
        setHasMore(false);
      }
    } catch {
      if (currentPage === 0) setContracts([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setContracts([]);
    setPage(0);
    setHasMore(false);
  }, [activeWalletId]);

  useEffect(() => {
    fetchContracts(activeWalletId, page);
  }, [activeWalletId, page, fetchContracts]);

  const handleLoadMore = () => setPage((prev) => prev + 1);

  const handleChildTransaction = async (data) => {
    setParentData(data);
    let res = await walletChildTransaction(data?.id);
    if (res?.data?.code === 200) {
      setChildTransaction(res?.data?.data || []);
    } else {
      ErrorMessage(res?.data?.message || "Something went wrong");
    }
  };

  const handleSelect = () => {};

  return (
    <>
      {
        <ChildTransaction
          open={childTransaction?.length > 0}
          handleClose={() => setChildTransaction([])}
          data={childTransaction}
          parentData={parentData}
        />
      }
      <div style={{ width: "100%", height: "100%" }}>
        <DetailHeader
          heading={"Contracts"}
          handleSelect={handleSelect}
          list={
            [
              // { name: "All", value: "all", status: true },
              // { name: "Ongoing", value: "ongoing", status: false },
              // { name: "Completed", value: "completed", status: false },
              // { name: "Pending", value: "pending", status: false },
            ]
          }
        />
        <div className={Styles.fullContractWrapper}>
          {/* HEADER */}
          <div className={Styles.fullContractHeader}>
            <div className={Styles.fullColLg}>Contract Id</div>
            <div className={Styles.fullColSm}>Self Listing</div>
            <div className={Styles.fullColMd}>Counterparty</div>
            <div className={Styles.fullColLg}>Duration</div>
            <div className={Styles.fullColMd}>Total Contract Value (₹)</div>
            <div className={Styles.fullColMd}>Amount Blocked (₹)</div>
            <div className={Styles.fullColMd}>
              Total Contract Schedule (MWh)
            </div>
            <div className={Styles.fullColMd}>Actual Delivery (MWh)</div>
            <div className={Styles.fullColSm}>Schedule</div>
            <div className={Styles.fullAction}>Details</div>
          </div>

          {/* BODY */}
          <div className={Styles.fullContractBody}>
            <LoadMorePagination
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              loading={loading}
            >
              {contracts?.map((ele) => {
                /* DELIVERY PERCENTAGE */
                const deliveryPercentage = Math.min(
                  ((ele?.deliveredVolume || 0) / (ele?.scheduledVolume || 1)) *
                    100,
                  100,
                );

                /* DELIVERY GRADIENT */
                const getDeliveryGradient = (percentage) => {
                  if (percentage <= 30) {
                    return "linear-gradient(90deg, #f97316, #ea580c)";
                  }

                  if (percentage <= 70) {
                    return "linear-gradient(90deg, #f59e0b, #facc15)";
                  }

                  if (percentage < 100) {
                    return "linear-gradient(90deg, #84cc16, #22c55e)";
                  }

                  return "linear-gradient(90deg, #16a34a, #15803d)";
                };

                return (
                  <div className={`${Styles.fullContractRow}`} key={ele?.id}>
                    {/* CONTRACT ID */}
                    <div className={Styles.fullColLg}>
                      <div className={Styles.fullPrimaryText}>
                        {ele?.bilateralContractUniqueId || "-"}
                      </div>
                    </div>

                    {/* SELF LISTING */}
                    <div className={Styles.fullColSm}>
                      <div className={Styles.fullPrimaryText}>
                        {ele?.tenderName?.split("_")?.[0] ||
                          (activeRole === "consumer"
                            ? ele?.name?.split("-")?.[0]?.includes("C")
                              ? ele?.name?.split("-")?.[0]
                              : ele?.name?.split("-")?.[1]
                            : ele?.name?.split("-")?.[1]?.includes("G")
                              ? ele?.name?.split("-")?.[1]
                              : ele?.name?.split("-")?.[0]
                          )?.split("_")?.[0] ||
                          "-"}
                      </div>
                    </div>

                    {/* COUNTERPARTY */}
                    <div className={Styles.fullColMd}>
                      <div className={Styles.fullPrimaryText}>
                        {activeRole === "consumer"
                          ? ele?.generatorName || "-"
                          : activeRole === "generator"
                            ? ele?.consumerName || "-"
                            : `${ele?.consumerName}-${ele?.generatorName}`}
                      </div>
                    </div>

                    {/* DATE */}
                    <div className={Styles.fullColLg}>
                      <div className={Styles.fullPrimaryText}>
                        {formatDateType(ele?.startDate)}
                      </div>

                      <div className={Styles.fullSecondaryText}>
                        to {formatDateType(ele?.endDate)}
                      </div>
                    </div>

                    {/* VALUE */}
                    <div className={Styles.fullColMd}>
                      <div
                        className={Styles.fullAmount}
                        title={ele?.scheduledAmount}
                      >
                        ₹ {(ele?.scheduledAmount || 0)?.toFixed(2)}
                      </div>
                    </div>

                    {/* BLOCKED */}
                    <div className={Styles.fullColMd}>
                      <div className={Styles.fullBlocked}>
                        ₹ {(ele?.amountBlocked || 0)?.toFixed(2)}
                      </div>
                    </div>

                    {/* SCHEDULE */}
                    <div className={Styles.fullColMd}>
                      <div className={Styles.fullMetric}>
                        {(ele?.scheduledVolume || 0)?.toFixed(2)} MWH
                      </div>
                    </div>

                    {/* DELIVERY */}
                    <div className={Styles.fullColMd}>
                      <div className={Styles.fullDeliveryWrapper}>
                        <div className={Styles.fullDeliveryBar}>
                          <div
                            className={Styles.fullDeliveryFill}
                            style={{
                              width: `${deliveryPercentage}%`,
                              background:
                                getDeliveryGradient(deliveryPercentage),
                            }}
                          />
                        </div>

                        <span className={Styles.fullDeliveryText}>
                          {(ele?.deliveredVolume || 0)?.toFixed(0)} MWH
                        </span>
                      </div>
                    </div>

                    {/* STATUS */}
                    <div className={Styles.fullColSm}>
                      <div
                        className={`${Styles.fullStatus} ${
                          ele?.status === "COMPLETED"
                            ? Styles.fullCompleted
                            : ele?.status === "PENDING"
                              ? Styles.fullOngoing
                              : Styles.fullRejected
                        }`}
                      >
                        {ele?.status === "COMPLETED"
                          ? "Completed"
                          : ele?.status === "PENDING"
                            ? "Ongoing"
                            : "Rejected"}
                      </div>
                    </div>

                    {/* ACTION */}
                    <div
                      className={Styles.fullAction}
                      onClick={() => handleChildTransaction(ele)}
                    >
                      <div className={Styles.fullViewButton}>
                        <VisibilityIcon className={Styles.fullViewIcon} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </LoadMorePagination>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contracts;
